import { Schemas } from '~/api/gen'
import { SeeMoreButton } from '~/components/elements/SeeMoreButton'
import { Coordinate3ColumnList } from '~/components/modules/CoordinatesList/Coordinate3ColumnList/Coordinate3ColumnList'
import { useSearchCoordinates } from '~/features/shared/search/hooks/useSearchCoordinates'
import { useIsDesktop } from '~/hooks/useMediaQuery'

type Props = {
  coordinateId: number
  member: Pick<Schemas.member_small, 'nick_name' | 'id' | 'user_name'>
}
type ViewProps = {
  nickname: string
  userName: string
  coordinates: Schemas.coordinate_search_v2[]
  displayMoreButton: boolean
}

const FETCH_COORDINATE_COUNT = 10
const DISPLAY_COORDINATE_COUNT = 9
const DISPLAY_COORDINATE_COUNT_FOR_DESKTOP = 6

export const RecentMemberCoordinateView: React.FC<ViewProps> = ({
  nickname,
  userName,
  coordinates,
  displayMoreButton,
}) => (
  <section className="relative">
    <div className="flex items-center justify-between gap-2 pl-4 pr-[18px] xl:pl-0">
      <h2 className="flex overflow-hidden text-[14px] font-bold xl:w-full xl:text-[13px]">
        <span className="truncate">{nickname}</span>
        <span className="shrink-0">の他のコーディネート</span>
      </h2>
      {displayMoreButton && (
        <p className="whitespace-nowrap leading-none xl:hidden">
          <a
            href={`/${userName}/`}
            className="text-[14px] font-bold text-blue-500"
            aria-label={`${nickname}の他のコーディネートをすべて見る`}
          >
            すべて見る
          </a>
        </p>
      )}
    </div>
    <div className="pt-2 xl:pt-3">
      <Coordinate3ColumnList coordinates={coordinates} />
    </div>
    {displayMoreButton && (
      <div className="hidden xl:flex xl:justify-end xl:pt-7">
        <div className="w-[100px]">
          <SeeMoreButton
            href={`/${userName}/`}
            aria-label={`${nickname}の他のコーディネートをもっと見る`}
          />
        </div>
      </div>
    )}
  </section>
)

export const RecentMemberCoordinate: React.FC<Props> = ({
  coordinateId,
  member,
}) => {
  const { data } = useSearchCoordinates({
    'member_ids[]': [member.id],
    sort_type: 'newest_to_oldest',
    pagesize: FETCH_COORDINATE_COUNT,
  })
  const isDesktop = useIsDesktop()

  if (!data) return null

  const displayCount = isDesktop
    ? DISPLAY_COORDINATE_COUNT_FOR_DESKTOP
    : DISPLAY_COORDINATE_COUNT

  const coordinates = data.coordinates
    .filter((coordinate) => coordinate.id !== coordinateId)
    .slice(0, displayCount)
  const coordiantesCount = data.total_count - 1

  if (coordinates.length === 0) return null

  return (
    <RecentMemberCoordinateView
      nickname={member.nick_name}
      userName={member.user_name}
      coordinates={coordinates}
      displayMoreButton={displayCount < coordiantesCount}
    />
  )
}
